/**
 * Демонстрационный аккаунт.
 *
 * Нужен, чтобы приложение можно было оценить до подключения сервера и без
 * регистрации. Создаётся один раз при первом нажатии «Посмотреть демо» и
 * наполняется осмысленным, а не случайным содержимым: цели связаны с
 * задачами и привычками, задачи разложены по дню вокруг сна и еды. Пустой
 * интерфейс не показывает методологию — наполненный показывает.
 */

import { seedNewUser } from '@/entities/profile/model/defaults';
import { db } from '@/shared/db/database';
import { getCurrentUserId, setCurrentUserId } from '@/shared/db/session-context';

import { localAuthProvider } from '../api/auth-provider';

export const DEMO_CREDENTIALS = {
  email: 'demo@kairo.app',
  password: 'kairo2026',
} as const;

/**
 * Идущая подготовка. Второй вызов присоединяется к ней вместо своей.
 *
 * Кнопки «Посмотреть демо» и «Тестовый пользователь» обе заводят демо-аккаунт,
 * и работа это долгая. Нажать одну, потом быстро вторую — обычное дело, и тогда
 * две подготовки шли внахлёст: обе видели «аккаунта ещё нет», обе принимались
 * его создавать, вторая регистрация падала на занятой почте. А та, что кончала
 * первой, своим `finally` возвращала сессию прямо посреди наполнения соседней.
 *
 * Замок не нужен: достаточно отдать второму вызову то же самое обещание.
 */
let preparing: Promise<void> | null = null;

/**
 * Гарантирует существование демо-аккаунта.
 *
 * Повторные вызовы безопасны: если аккаунт уже есть, ничего не происходит, а
 * если подготовка идёт прямо сейчас — вызов дожидается её, а не запускает свою.
 */
export function ensureDemoAccount(): Promise<void> {
  preparing ??= prepareDemoAccount().finally(() => {
    // Сбрасывается в любом исходе: после сбоя следующая попытка обязана
    // начаться заново, а не получить навсегда отвергнутое обещание.
    preparing = null;
  });

  return preparing;
}

async function prepareDemoAccount(): Promise<void> {
  const existing = await db.users.where('email').equals(DEMO_CREDENTIALS.email).first();
  if (existing) return;

  const session = await localAuthProvider.register({
    ...DEMO_CREDENTIALS,
    name: 'Анна',
  });

  /*
    Наполнение пишется от имени созданного пользователя, а по окончании сессия
    возвращается к тому, что было.

    Здесь стоял жёсткий `null` под именем `previousUserId` — то есть подготовка
    демо всегда сбрасывала текущего пользователя, кто бы им ни был. Пока это
    вызывается только с экрана входа, где сессии нет, разницы не видно; но
    любой вызов при живой сессии молча выбивал бы её из-под работающих запросов.
  */
  const previousUserId = getCurrentUserId();
  setCurrentUserId(session.user.id);
  try {
    await seedNewUser(session.user.name);
    const { seedDemoContent } = await import('./demo-content');
    await seedDemoContent();
  } finally {
    setCurrentUserId(previousUserId);
  }
}

/** Вошли ли мы сейчас под демо-аккаунтом. */
export function isDemoEmail(email: string | undefined): boolean {
  return email === DEMO_CREDENTIALS.email;
}

/**
 * Перезаполняет демо-аккаунт с нуля.
 *
 * Демо наполняется один раз, при создании аккаунта. Из этого следует
 * неочевидное: всё, что мы позже добавляем в демо, к тем, кто уже входил,
 * никогда не попадает — они видят наполнение той версии, в которой завели
 * аккаунт. Отсюда и эта кнопка: без неё изменения демо доезжают только до
 * новых устройств.
 *
 * Данные стираются полностью, включая созданное вручную. Это не мягкое
 * удаление: демо на то и демо, что его содержимое — образец, а не журнал, и
 * копить в нём слои от разных версий бессмысленно. Предупредить об этом
 * обязана вызывающая сторона.
 */
export async function resetDemoContent(userId: string, name: string): Promise<void> {
  await db.wipeUserData(userId);

  const previousUserId = getCurrentUserId();
  setCurrentUserId(userId);
  try {
    await seedNewUser(name);
    const { seedDemoContent } = await import('./demo-content');
    await seedDemoContent();
  } finally {
    setCurrentUserId(previousUserId);
  }
}
